import React, { useState } from "react";
import NavBar from "../components/NavBar";

const HOUSEHOLD_API_URL = "/api/households";

function HouseholdRegistration() {
  const [step, setStep] = useState("form");
  const [loading, setLoading] = useState(false);
  const [householdSize, setHouseholdSize] = useState("");
  const [token, setToken] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  async function handleRegister() {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await fetch(`${HOUSEHOLD_API_URL}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolId: "RW",
          householdSize: parseInt(householdSize) || null,
        }),
      });
      if (!res.ok) throw new Error(`Registration failed: ${res.status}`);
      const data = await res.json();
      setToken(data.tokenId);
      setQrUrl(data.qrCode);
      setStep("success");
    } catch (err) {
      setErrorMsg(err.message);
      setStep("error");
    } finally {
      setLoading(false);
    }
  }

  if (step === "success") {
    return (
      <div style={styles.container}>
        <NavBar title="Register" />
        <div style={styles.card}>
          <div style={styles.successBadge}>
            {"\u2713"} Registration Complete
          </div>
          <h2 style={styles.heading}>Your Raider Depot Token</h2>
          <div style={styles.tokenDisplay}>{token}</div>
          <p style={styles.subtext}>
            Show this QR code or your token code at pick-up. No name required.
          </p>
          {qrUrl && <img src={qrUrl} alt="QR Code" style={styles.qrImage} />}

          {/* Fallback instructions for when QR scanning doesn't work */}
          <div style={styles.fallbackBox}>
            <p style={styles.fallbackTitle}>Can't scan the QR code?</p>
            <p style={styles.fallbackText}>
              Go to: <strong>riverwoodraiderdepot.org/order</strong>
            </p>
            <p style={styles.fallbackText}>
              Enter your code: <strong>{token}</strong>
            </p>
          </div>

          <p style={styles.helpText}>Need help? See your school counselor.</p>

          <p style={styles.hint}>
            Screenshot or print this page before closing.
          </p>
          <button style={styles.secondaryButton} onClick={() => window.print()}>
            Print / Save
          </button>
        </div>

        {/* Print-only styles for clean card output */}
        <style>{`
          @media print {
            body { margin: 0; padding: 0; }
            nav, button, .no-print { display: none !important; }
            div[style] { box-shadow: none !important; }
          }
        `}</style>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div style={styles.container}>
        <NavBar title="Register" />
        <div style={styles.card}>
          <div style={styles.errorBadge}>Registration Error</div>
          <p style={styles.subtext}>{errorMsg}</p>
          <button style={styles.button} onClick={() => setStep("form")}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <NavBar title="Register" />
      <div style={styles.card}>
        <div style={styles.logo}>{"\uD83D\uDED2"}</div>
        <h1 style={styles.title}>Raider Depot</h1>
        <p style={styles.subtext}>
          Register to receive your anonymous pickup token. No personal
          information is collected or stored.
        </p>
        <div style={styles.privacyNote}>
          {"\uD83D\uDD12"} Your privacy is protected. This system is anonymous
          by design.
        </div>
        <div style={{ marginBottom: "20px", textAlign: "left" }}>
          <label
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#1a365d",
              display: "block",
              marginBottom: "6px",
            }}
          >
            Household size{" "}
            <span style={{ fontWeight: "400", color: "#888" }}>
              (number of people)
            </span>
          </label>
          <select
            value={householdSize}
            onChange={(e) => setHouseholdSize(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 14px",
              fontSize: "16px",
              border: "1.5px solid #e2e8f0",
              borderRadius: "8px",
              backgroundColor: "#fff",
              color: "#1a365d",
              fontWeight: "600",
              appearance: "auto",
              cursor: "pointer",
            }}
          >
            <option value="">Select...</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? "person" : "people"}
              </option>
            ))}
          </select>
        </div>
        <button
          style={
            loading || !householdSize ? styles.buttonDisabled : styles.button
          }
          onClick={handleRegister}
          disabled={loading || !householdSize}
        >
          {loading ? "Registering..." : "Get My Token"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f4f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "Arial, sans-serif",
    padding: "20px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "40px",
    maxWidth: "420px",
    width: "100%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    textAlign: "center",
  },
  logo: { fontSize: "48px", marginBottom: "8px" },
  title: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#1a365d",
    margin: "0 0 12px 0",
  },
  heading: {
    fontSize: "22px",
    fontWeight: "bold",
    color: "#1a365d",
    margin: "0 0 16px 0",
  },
  subtext: {
    fontSize: "15px",
    color: "#555",
    lineHeight: "1.6",
    margin: "0 0 24px 0",
  },
  privacyNote: {
    backgroundColor: "#ebf8ff",
    border: "1px solid #bee3f8",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "14px",
    color: "#2b6cb0",
    marginBottom: "24px",
  },
  tokenDisplay: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#2b6cb0",
    backgroundColor: "#ebf8ff",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
    letterSpacing: "2px",
  },
  qrImage: {
    width: "200px",
    height: "200px",
    margin: "16px auto",
    display: "block",
    border: "4px solid #e2e8f0",
    borderRadius: "8px",
  },
  hint: { fontSize: "13px", color: "#888", marginBottom: "20px" },
  button: {
    backgroundColor: "#2b6cb0",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    padding: "14px 32px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    width: "100%",
  },
  buttonDisabled: {
    backgroundColor: "#a0aec0",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    padding: "14px 32px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "not-allowed",
    width: "100%",
  },
  secondaryButton: {
    backgroundColor: "#ffffff",
    color: "#2b6cb0",
    border: "2px solid #2b6cb0",
    borderRadius: "8px",
    padding: "12px 32px",
    fontSize: "15px",
    fontWeight: "bold",
    cursor: "pointer",
    width: "100%",
  },
  successBadge: {
    backgroundColor: "#c6f6d5",
    color: "#276749",
    borderRadius: "20px",
    padding: "6px 16px",
    fontSize: "14px",
    fontWeight: "bold",
    display: "inline-block",
    marginBottom: "16px",
  },
  errorBadge: {
    backgroundColor: "#fed7d7",
    color: "#9b2335",
    borderRadius: "20px",
    padding: "6px 16px",
    fontSize: "14px",
    fontWeight: "bold",
    display: "inline-block",
    marginBottom: "16px",
  },
  fallbackBox: {
    backgroundColor: "#f7fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
    textAlign: "center",
  },
  fallbackTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#1a365d",
    margin: "0 0 8px 0",
  },
  fallbackText: {
    fontSize: "14px",
    color: "#4a5568",
    margin: "0 0 4px 0",
    lineHeight: "1.6",
  },
  helpText: {
    fontSize: "13px",
    color: "#718096",
    marginBottom: "12px",
    fontStyle: "italic",
  },
};

export default HouseholdRegistration;
